import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import React, { FC, useContext, useState } from 'react'
import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList
} from 'front-end/src/components'
import { Column } from 'react-table'
import { css } from '@emotion/react'
import { Trans, t } from '@lingui/macro'
import {
  SqlOrderDirectionEnum,
  WorkbooksOverviewOrderColumn,
  WorkbookAssignedFilterEnum,
  WorkbookCompletedFilterEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import Select, {
  SelectVariant
} from 'front-end/src/components/common/select/Select'
import { formatDateShort } from 'front-end/src/utils/dateHelpers'
import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import UserAvatar from '../../../components/common/user/user-avatar/UserAvatar'
import WorkbookArchiveModal from './WorkbookArchiveModal'
import { AccessibilityLabel } from '../../../components/common/AccessibilityLabel'
import { DownloadWorkbooksCSV } from './DownloadWorkbooksCSV'
import {
  formatTimeInSec,
  getAttemptString,
  getPagesCount,
  getScoreString
} from '../utils'
import { IBaseOverviewFilter } from '../interfaces'
import { useWorkbooksOverviewQuery } from './WorkbooksAnalytics.codegen'
import { EllipsisWithTippy } from '../EllipsisWithTooltip'
import IconInfo from '../../../components/common/icon/IconInfo'
import { AnalyticsContext } from '../AnalyticsContext'
import {
  StatusArchivedGrey,
  StatusDraftGrey,
  StatusProgressFailed,
  StatusProgressPassed
} from 'front-end/src/components/common/status/Status'
import IconDotsVertical from 'front-end/src/components/common/icon/IconDotsVertical'
import { linkBuilder } from 'shared/linkBuilder'
import { IconArchive } from 'front-end/src/components/common/icon/IconArchive'
import IconCheck from 'front-end/src/components/common/icon/IconCheck'
import { useMarkAsCompleteMutation } from './WorkbooksOverviewTable.codegen'
import { WorkbookStatusEnum } from 'front-end/src/graphql/public/PublicSchemaTypes'
import { useDateFilterContext } from '../../../components/common/dateFilter'
import IconQuestion from 'front-end/src/components/common/icon/IconQuestion'

interface IProps {
  resourceId?: number
  userId?: number
  showTime?: boolean
}

const archiveWorkbookModalInitState = {
  show: false,
  workbookId: null
}

interface IArchiveWorkbookModalState {
  show: boolean
  workbookId: number | null
}

export interface IWorkbooksTableFilter extends IBaseOverviewFilter {
  orderColumn: WorkbooksOverviewOrderColumn
  assigned: WorkbookAssignedFilterEnum
  completed: WorkbookCompletedFilterEnum
  resourceId: number | null | undefined
}

const WorkbooksOverviewTable: FC<IProps> = ({
  userId,
  resourceId,
  showTime = false
}: IProps) => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [archiveWorkbookModalState, setArchiveWorkbookModalState] =
    useState<IArchiveWorkbookModalState>(archiveWorkbookModalInitState)

  const [workbooksTableFilters, setWorkbooksTableFilters] =
    useState<IWorkbooksTableFilter>({
      limit: 20,
      orderColumn: WorkbooksOverviewOrderColumn.startDate,
      orderDirection: SqlOrderDirectionEnum.DESC,
      assigned: WorkbookAssignedFilterEnum.ALL,
      completed: WorkbookCompletedFilterEnum.ALL,
      userId,
      resourceId
    })
  const [{ from, to }] = useDateFilterContext()

  const overviewTableQuery = {
    ...workbooksTableFilters,
    page: filter.page,
    search: filter.search
  }

  const {
    data,
    loading,
    refetch: refetchTable
  } = useWorkbooksOverviewQuery({
    variables: {
      mainAnalyticsFilter: {
        ...filterAsQueryVariables,
        from,
        to
      },
      workbooksOverviewFilter: overviewTableQuery
    }
  })

  const [markAsComplete] = useMarkAsCompleteMutation()

  let pagesCount = 1

  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.workbooks.workbooksCount,
      overviewTableQuery.limit
    )
  }

  const onAssignmentChange = (opt: { value: WorkbookAssignedFilterEnum }) => {
    setWorkbooksTableFilters((prevState) => {
      return {
        ...prevState,
        page: 1,
        assigned: opt.value
      }
    })
  }

  const onCompletionChange = (opt: { value: WorkbookCompletedFilterEnum }) => {
    setWorkbooksTableFilters((prevState) => {
      return {
        ...prevState,
        page: 1,
        completed: opt.value
      }
    })
  }

  const tableColumns: Array<Column<any>> = [
    {
      Header: '',
      width: 40,
      sortable: false,
      Cell: (row) => {
        if (
          row.original.enrolment.share_batch_ts ||
          row.original.enrolment.target_completion_date
        ) {
          return (
            <Tooltip
              text={
                <>
                  {row.original.enrolment.share_batch_ts && (
                    <Box>
                      <Trans>Shared on</Trans>:{' '}
                      {formatDateShort(row.original.enrolment.share_batch_ts)}
                    </Box>
                  )}

                  {row.original.enrolment.target_completion_date && (
                    <strong>
                      <Trans>Due date</Trans>:{' '}
                      {formatDateShort(
                        row.original.enrolment.target_completion_date
                      )}
                    </strong>
                  )}
                </>
              }
            >
              <IconInfo />
            </Tooltip>
          )
        }
        return null
      }
    },
    {
      Header: t`Started`,
      maxWidth: 140,
      width: 120,
      accessor: 'enrolment.start_date',
      id: WorkbooksOverviewOrderColumn.startDate,
      Cell: (row) => formatDateShort(row.value) || t`Not started`
    },
    {
      Header: t`Completed`,
      maxWidth: 160,
      width: 120,
      className: 'date',
      accessor: 'enrolment.completion_date',
      id: WorkbooksOverviewOrderColumn.completionDate,
      Cell: (row) => <span className="green">{formatDateShort(row.value)}</span>
    },
    {
      Header: t`Attempt`,
      maxWidth: 100,
      id: WorkbooksOverviewOrderColumn.attempt,
      Cell: (row) => (
        <span>
          {row.original.enrolment.topicVersion?.version ? (
            <>
              {getAttemptString(
                row.original.enrolment.attempt,
                row.original.enrolment.topicVersion?.max_attempts,
                !!row.original.enrolment.topicVersion?.allow_retry
              )}

              {!row.original.enrolment.topicVersion?.allow_retry &&
                row.original.enrolment.attempt > 1 && (
                  <Tooltip
                    text={
                      <Trans>
                        The number of attempts shows as higher than one because
                        there are archived enrolments included in this total
                      </Trans>
                    }
                  >
                    <IconQuestion ml="4px" position="relative" top="1px" />
                  </Tooltip>
                )}
            </>
          ) : (
            ''
          )}
        </span>
      )
    },
    {
      Header: t`Score`,
      className: 'score',
      accessor: 'enrolment.auto_correct',
      id: WorkbooksOverviewOrderColumn.score,
      maxWidth: 120,
      width: 80,
      Cell: (row) => {
        return (
          <Box>
            {getScoreString(
              row.original.enrolment.auto_correct,
              row.original.enrolment.available_points
            )}
          </Box>
        )
      }
    },
    {
      Header: t`Time`,
      show: showTime,
      maxWidth: 100,
      width: 80,
      accessor: 'enrolment.progress_time',
      id: WorkbooksOverviewOrderColumn.progress_time,
      Cell: (row) => <>{formatTimeInSec(row.value)}</>
    },
    {
      Header: t`Result`,
      accessor: 'enrolment.available_points',
      className: '',
      sortable: false,
      width: 90,
      Cell: (row) => {
        const hasScore = !!row.value
        const hasPassMark = hasScore && row.original.enrolment.pass_mark > 0
        const hasPassed =
          hasPassMark &&
          row.original.enrolment.auto_correct >=
            row.original.enrolment.pass_mark

        const hasCompleted = !!row.original.enrolment.completion_date

        return (
          <Box className="result" overflow="hidden">
            {hasCompleted && hasPassMark && (
              <>
                {hasPassed ? (
                  <StatusProgressPassed />
                ) : (
                  <StatusProgressFailed />
                )}
              </>
            )}
          </Box>
        )
      }
    },
    {
      Header: t`Version`,
      sortable: false,
      width: 55,
      Cell: (row) => row.original.enrolment.topicVersion?.version ?? ''
    },
    {
      Header: '',
      sortable: false,
      className: 'menu',
      width: 70,
      Cell: (row) => {
        return (
          !row.original.enrolment.deleted_at && (
            <Flex>
              <Menu>
                <MenuButton
                  circular
                  boxSize="37px"
                  bg={'transparent'}
                  border={0}
                  dataTest="KebabMenu"
                  onClick={(e) => {
                    e.stopPropagation() // Stop propagating click on row
                  }}
                >
                  <IconDotsVertical boxSize="18px" />
                </MenuButton>
                <MenuList data-test="MenuList">
                  <MenuItem
                    icon={<IconArchive />}
                    data-test="ArchiveButton"
                    onClick={async (clickEvent) => {
                      clickEvent.stopPropagation()
                      setArchiveWorkbookModalState({
                        show: true,
                        workbookId: row.original.enrolment.id
                      })
                    }}
                  >
                    {t`Archive enrolment`}
                  </MenuItem>
                  {row.original.enrolment.status !==
                    WorkbookStatusEnum.COMPLETED && (
                    <MenuItem
                      icon={<IconCheck fontSize="18px" />}
                      data-test="CompleteEnrolmentButton"
                      onClick={async (clickEvent) => {
                        clickEvent.stopPropagation()
                        await markAsComplete({
                          variables: {
                            filter: filterAsQueryVariables,
                            workbookId: row.original.enrolment.id
                          }
                        })
                        refetchTable()
                      }}
                    >
                      {t`Complete enrolment`}
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            </Flex>
          )
        )
      }
    }
  ]

  if (!resourceId) {
    tableColumns.unshift({
      Header: t`Resource`,
      maxWidth: 350,
      id: WorkbooksOverviewOrderColumn.resourceName,
      accessor: WorkbooksOverviewOrderColumn.resourceName,
      className: 'tooltip',
      Cell: (row) => {
        return (
          <>
            <EllipsisWithTippy tippyText={row.value}>
              <strong>{row.value}</strong>
            </EllipsisWithTippy>
            {row.original.enrolment.deleted_at && <StatusArchivedGrey />}
            {!row.original.enrolment.topic.production_topic_version_id &&
              !row.original.enrolment.deleted_at && <StatusDraftGrey />}
          </>
        )
      }
    })
  }

  if (!userId) {
    tableColumns.unshift({
      Header: t`Student`,
      maxWidth: 310,
      id: WorkbooksOverviewOrderColumn.displayName,
      accessor: 'enrolment.user.display_name',
      Cell: (row) => (
        <>
          <UserAvatar
            user={{
              display_name: row.value,
              image_url: row.original.enrolment.user.image_url
            }}
          />
          <Box className="overflowEllipsis">{row.value}</Box>
        </>
      )
    })
  }

  const assignedOpts = [
    {
      value: WorkbookAssignedFilterEnum.ASSIGNED,
      label: t`Shared only`
    },
    {
      value: WorkbookAssignedFilterEnum.SELF_ENROLLED,
      label: t`Self-assigned only`
    },
    { value: WorkbookAssignedFilterEnum.ALL, label: t`Share or self-assign` }
  ]
  const completedOpts = [
    {
      value: WorkbookCompletedFilterEnum.COMPLETED,
      label: t`Completed`
    },
    {
      value: WorkbookCompletedFilterEnum.NOT_COMPLETED,
      label: t`Not completed`
    },
    { value: WorkbookCompletedFilterEnum.ALL, label: t`All` }
  ]
  return (
    <div
      css={css`
        label .Select__control {
          margin-left: 10px;
        }
      `}
    >
      <TableExtendedOld
        loading={loading}
        manual
        data={
          data && data.analytics && data.analytics.workbooks.workbooksOverview
        }
        tableHeader={t`Enrolments`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        defaultPageSize={overviewTableQuery.limit}
        showPageSizeOptions={false}
        showSearchFilter
        extendFilter={
          <>
            <AccessibilityLabel text={t`Assigned`}>
              <Select
                variant={SelectVariant.GREY}
                options={assignedOpts}
                // @ts-expect-error 9/11/2020
                onChange={onAssignmentChange}
                value={assignedOpts.find(
                  ({ value }) => overviewTableQuery.assigned == value
                )}
              />
            </AccessibilityLabel>

            <AccessibilityLabel text={t`Completed`}>
              <Select
                options={completedOpts}
                // @ts-expect-error 9/11/2020
                onChange={onCompletionChange}
                value={completedOpts.find(
                  ({ value }) => overviewTableQuery.completed == value
                )}
              />
            </AccessibilityLabel>
          </>
        }
        resizable={false}
        minRows={1}
        pages={pagesCount}
        getTrProps={(_state, rowInfo) => ({
          onClick: () => {
            navigateToAndResetPageAndSearch(
              linkBuilder.analyticsWorkbooksDetail(
                rowInfo.original.enrolment.id
              ),
              changeFilter
            )
          },
          className: `clickable ${
            rowInfo && rowInfo.original.enrolment.deleted_at ? 'archived' : ''
          }`,
          ...DEFAULT_TR_PROPS
        })}
        paginationContentAfter={
          data &&
          data.analytics && (
            <>
              <DownloadWorkbooksCSV
                workbooksOverviewFilter={overviewTableQuery}
                workbooksCount={data.analytics.workbooks.workbooksCount}
              />
            </>
          )
        }
        defaultSortDesc
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setWorkbooksTableFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as WorkbooksOverviewOrderColumn)
                : WorkbooksOverviewOrderColumn.resourceName,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />

      {archiveWorkbookModalState.show && (
        <WorkbookArchiveModal
          workbookId={archiveWorkbookModalState.workbookId as number}
          onArchived={() => {
            setArchiveWorkbookModalState(archiveWorkbookModalInitState)
            refetchTable()
          }}
          onCanceled={() => {
            setArchiveWorkbookModalState(archiveWorkbookModalInitState)
          }}
        />
      )}
    </div>
  )
}

export default WorkbooksOverviewTable
